import { ArrowLeft, Ban } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { QuickBooksSyncJobPayload } from '@ankor-io/common/types/integrations/quickbooks'
import { IntegrationKey } from '@ankor-io/common/types/integrations/types'
import type { XeroSyncJobPayload } from '@ankor-io/common/types/integrations/xero/types'
import {
  Badge,
  Button,
  CopyToClipboard,
  Loading,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ankor-io/ui-components'

import { useQuickBooksSyncJobsQuery, useXeroConnectionsQuery, useXeroSyncJobsQuery } from '@/queries/organization'
import { formatDate, formatDuration } from '@/utils/format'

import { statusBadge, stepStatusIcon } from './integration-status'
import { mapQuickBooksSyncJob, mapXeroSyncJob } from './integration-sync-jobs.mapper'

const mappedSyncJobs = {
  [IntegrationKey.XERO_EXPENSES]: {
    name: 'Xero',
    accountColumn: 'Tenant',
    missingConnection: 'No Xero connection found for this organization.',
    toRows: (jobs?: XeroSyncJobPayload[]) => jobs?.map(mapXeroSyncJob),
  },
  [IntegrationKey.QUICKBOOKS]: {
    name: 'QuickBooks',
    accountColumn: 'Company',
    missingConnection: undefined,
    toRows: (jobs?: QuickBooksSyncJobPayload[]) => jobs?.map(mapQuickBooksSyncJob),
  },
}

export function IntegrationSyncJobsView({
  organizationId,
  integrationKey,
  onBack,
  onSelectJob,
  onSelectLastRun,
}: {
  organizationId: string
  integrationKey: IntegrationKey
  onBack: () => void
  onSelectJob?: (jobId: string) => void
  onSelectLastRun?: (jobId: string, runId: string) => void
}) {
  const integration = mappedSyncJobs[integrationKey]
  const isXero = integrationKey === IntegrationKey.XERO_EXPENSES
  const isQuickBooks = integrationKey === IntegrationKey.QUICKBOOKS
  const [vesselFilter, setVesselFilter] = useState<string>('all')
  const { data: connections, isPending: areConnectionsPending } = useXeroConnectionsQuery(organizationId, {
    enabled: isXero,
  })
  const connectionId = connections?.[0]?.id
  const {
    data: xeroJobs,
    isPending: isXeroJobsPending,
    error: xeroJobsError,
  } = useXeroSyncJobsQuery(organizationId, connectionId, { enabled: isXero })
  const quickBooksJobs = useQuickBooksSyncJobsQuery(organizationId, { enabled: isQuickBooks })

  const loaded = {
    [IntegrationKey.XERO_EXPENSES]: {
      jobs: mappedSyncJobs[IntegrationKey.XERO_EXPENSES].toRows(xeroJobs),
      isPending: areConnectionsPending || isXeroJobsPending,
      error: xeroJobsError,
    },
    [IntegrationKey.QUICKBOOKS]: {
      jobs: mappedSyncJobs[IntegrationKey.QUICKBOOKS].toRows(quickBooksJobs.data),
      isPending: quickBooksJobs.isPending,
      error: quickBooksJobs.error,
    },
  }
  const { jobs, isPending, error } = loaded[integrationKey]

  const vesselNames = useMemo(() => {
    if (!jobs) return []
    return [...new Set(jobs.map((job) => job.connectedAccountName).filter(Boolean))].sort()
  }, [jobs])

  const filteredJobs = useMemo(() => {
    if (!jobs) return []
    if (vesselFilter === 'all') return jobs
    return jobs.filter((job) => job.connectedAccountName === vesselFilter)
  }, [jobs, vesselFilter])

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button
            plain
            onClick={onBack}
            className="flex items-center"
          >
            <ArrowLeft data-slot="icon" />
            Back
          </Button>
          <span className="text-gray-300 dark:text-gray-600">/</span>
          <span className="text-sm font-medium text-gray-900 dark:text-white">{integration.name} — Sync Jobs</span>
          {jobs && (
            <Badge
              color="gray"
              className="whitespace-nowrap"
            >
              {filteredJobs.length} jobs
            </Badge>
          )}
        </div>
        {jobs && jobs.length > 0 && (
          <Select
            className="w-auto max-w-xs"
            value={vesselFilter}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setVesselFilter(e.target.value)}
            aria-label="Filter by vessel"
          >
            <option value="all">All vessels</option>
            {vesselNames.map((name) => (
              <option
                key={name}
                value={name}
              >
                {name}
              </option>
            ))}
          </Select>
        )}
      </div>
      {isPending && <Loading variant="spinner" />}
      {error && <div className="text-sm text-red-500">Error: {error.message}</div>}
      {integration.missingConnection && !isPending && connections && connections.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">{integration.missingConnection}</p>
      )}
      {jobs && jobs.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No sync jobs found.</p>}
      {jobs && jobs.length > 0 && (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Job</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Vessel</TableHeader>
              <TableHeader>{integration.accountColumn}</TableHeader>
              <TableHeader>Last Run</TableHeader>
              <TableHeader>Created At</TableHeader>
              <TableHeader>Duration</TableHeader>
              <TableHeader>Action</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredJobs.map((job) => (
              <TableRow
                key={job.id}
                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50"
                onClick={() => onSelectJob?.(job.id)}
              >
                <TableCell className="font-mono text-sm">
                  <Badge color="gray">{job.id}</Badge>
                </TableCell>
                <TableCell>{statusBadge(job.status)}</TableCell>
                <TableCell>{job.connectedAccountName || '—'}</TableCell>
                <TableCell>{job.accountName || '—'}</TableCell>
                <TableCell>
                  {job.lastRun ? (
                    <button
                      className="hover:opacity-70 transition-opacity"
                      onClick={(e) => {
                        e.stopPropagation()
                        onSelectLastRun?.(job.id, job.lastRun!.id)
                      }}
                    >
                      {stepStatusIcon(job.lastRun.status)}
                    </button>
                  ) : (
                    <Ban className="size-4 text-gray-400" />
                  )}
                </TableCell>
                <TableCell className="tabular-nums text-gray-500">{formatDate(job.startedAt)}</TableCell>
                <TableCell className="tabular-nums text-gray-500">{formatDuration(job.duration)}</TableCell>
                <TableCell>
                  <div onClick={(e) => e.stopPropagation()}>
                    <CopyToClipboard
                      text={job.id}
                      tooltip="Copy Job ID"
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
